import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { spacing, typography, animations } from '../../design-system/tokens';
import { LoadingState } from '../../design-system/components/feedback/LoadingState';
import { OnboardingFlow } from './OnboardingFlow';
import { userService } from '../../services/userService';
import { useProject } from '../../contexts/ProjectContext';

export interface OnboardingGuardProps {
  children: React.ReactNode;
}

const LoadingContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${spacing[4]};
  font-family: ${typography.fontFamilies.sans};
  animation: ${animations.fadeIn};
`;

export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { loadProjects } = useProject();
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      // Check if user has set up organization name
      const setupStatus = await userService.checkUserSetupStatus();
      
      if (setupStatus.isSetupComplete && setupStatus.hasOrganization) {
        // User has completed organization setup
        setNeedsOnboarding(false);
      } else {
        // User needs to go through onboarding
        setNeedsOnboarding(true);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      // If there's any error, show onboarding to be safe
      setNeedsOnboarding(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = async () => {
    // Organization name should already be set via onboarding flow
    // Project creation should be handled in OnboardingFlow, not here
    console.log('[OnboardingGuard] Onboarding completed, cleaning up...');
    
    // Just update the UI state and clear cache
    // Project reloading will be handled by OnboardingFlow after project creation
    setNeedsOnboarding(false);
    userService.clearCache();
    
    console.log('[OnboardingGuard] Cleanup completed');
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingState />
      </LoadingContainer>
    );
  }

  if (needsOnboarding) {
    return <OnboardingFlow onComplete={handleOnboardingComplete} />;
  }

  return <>{children}</>;
}; 